import * as XLSX from 'xlsx';

// Helper function to get backend data URL
const getDataUrl = (path) => {
  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';
  const url = `${API_BASE}/data/${path}`;
  console.log('🔗 Building data URL:', url);
  return url;
};

// Helper function to normalize store numbers for consistent matching
const normalizeStoreNumber = (storeNum) => {
  if (!storeNum) return '';
  
  // Remove leading zeros, spaces, and convert to string
  const cleaned = String(storeNum).trim().replace(/^0+/, '');
  
  return cleaned || String(storeNum).trim(); // Fallback to original if empty after cleaning
};

export const readMinMaxFile = async () => {
  console.log('🚨 MIN/MAX FUNCTION CALLED - START');
  console.log('🚨 This line should appear if function is called at all');
  
  try {
    console.log('=== READING SYSTEM MIN/MAX FILE (NEW FORMAT) ===');
    console.log('Format: Column A=Cust, B=Material, D=Target stock, F=Reorder point');
    
    const dataUrl = getDataUrl('New Min:Max.xlsx');
    console.log('Fetching Min/Max file from backend:', dataUrl);
    const response = await fetch(dataUrl);
    console.log('Response status:', response.status, 'OK:', response.ok);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    console.log('✓ File fetch successful');
    
    const arrayBuffer = await response.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    
    const sheetName = workbook.SheetNames[0];
    console.log('Sheet name:', sheetName);
    
    const jsonData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);
    console.log('📏 Total min/max rows loaded:', jsonData.length);
    
    // DEBUG: Log column headers for new format
    if (jsonData.length > 0) {
      console.log('Column headers found:', Object.keys(jsonData[0]));
      
      // Check for variations of Target stock and Reorder point
      const targetColumns = Object.keys(jsonData[0]).filter(col => 
        col.toLowerCase().includes('target') || col.toLowerCase().includes('stock')
      );
      const reorderColumns = Object.keys(jsonData[0]).filter(col => 
        col.toLowerCase().includes('reorder') || col.toLowerCase().includes('point')
      );
      
      console.log('📊 Target/Stock columns:', targetColumns);
      console.log('📊 Reorder/Point columns:', reorderColumns);
      
      console.log('First 3 raw rows (new format):');
      jsonData.slice(0, 3).forEach((row, i) => {
        console.log(`  Row ${i}:`, {
          'Cust.': row['Cust.'],
          'Material': row['Material'],
          'Target stock': row['Target stock'],
          'Reorder point': row['Reorder point'],
          targetColumns: targetColumns.map(col => `${col}: ${row[col]}`),
          reorderColumns: reorderColumns.map(col => `${col}: ${row[col]}`)
        });
      });
    }
    
    // Build lookup map: {store}-{sku} → {min, max}
    console.log('=== BUILDING MIN/MAX LOOKUP MAP (NEW FORMAT) ===');
    const minMaxLookup = new Map();
    let processedCount = 0;
    let skipCount = 0;
    
    // Helper function to get column value with fallbacks
    const getColumnValue = (row, possibleNames) => {
      for (const name of possibleNames) {
        if (row[name] !== undefined && row[name] !== null) {
          const value = parseFloat(row[name]);
          if (!isNaN(value)) {
            return value;
          }
        }
      }
      return 0;
    };
    
    jsonData.forEach((row, index) => {
      // New column mapping
      const store = row['Cust.'];  // Column A
      const sku = row['Material'];  // Column B  
      
      // Try multiple column name variations for robustness
      const systemMax = getColumnValue(row, [
        'Target stock',
        'Target Stock', 
        'Targetstock',
        'target stock'
      ]);
      
      const systemMin = getColumnValue(row, [
        'Reorder point',
        'Reorder Point',
        'Reorderpoint', 
        'reorder point'
      ]);
      
      if (store && sku) {
        // Clean store number (remove any leading zeros or spaces)
        const cleanStore = normalizeStoreNumber(store);
        const cleanSku = String(sku).trim();
        const key = `${cleanStore}-${cleanSku}`;
        
        const dataObject = {
          min: systemMin,
          max: systemMax
        };
        
        minMaxLookup.set(key, dataObject);
        processedCount++;
        
        // Log first 5 entries with detailed value inspection
        if (index < 5) {
          console.log(`Processing entry ${index + 1}:`, {
            originalStore: store,
            cleanStore: cleanStore,
            sku: cleanSku,
            systemMin: systemMin,
            systemMax: systemMax,
            key: key,
            dataObject: dataObject,
            rawTargetStock: row['Target stock'],
            rawReorderPoint: row['Reorder point']
          });
        }
      } else {
        skipCount++;
        if (skipCount <= 3) {
          console.log(`⚠️ Skipping row ${index + 1}: missing store or SKU`, { store, sku });
        }
      }
    });
    
    console.log('✅ Min/Max lookup built:', minMaxLookup.size, 'store-SKU combinations');
    console.log('📊 Processing summary:', { processedCount, skipCount });
    console.log('📝 Sample entries:', Array.from(minMaxLookup.entries()).slice(0, 3));
    console.log('🔑 Sample min/max keys from file:', Array.from(minMaxLookup.keys()).slice(0, 10));
    
    // Debug what's actually in the Map
    console.log('=== VERIFYING MAP CONTENTS ===');
    const firstKey = Array.from(minMaxLookup.keys())[0];
    const firstValue = minMaxLookup.get(firstKey);
    
    console.log('First key:', firstKey);
    console.log('First value:', firstValue);
    console.log('Value type:', typeof firstValue);
    console.log('Value properties:', Object.keys(firstValue || {}));
    
    // Test with known key from debug endpoint
    const testKey = '157-695';
    const testValue = minMaxLookup.get(testKey);
    console.log(`Test key "${testKey}":`, {
      exists: minMaxLookup.has(testKey),
      value: testValue,
      min: testValue?.min,
      max: testValue?.max,
      allProps: testValue ? Object.keys(testValue) : 'no value'
    });
    
    return minMaxLookup;
  } catch (error) {
    console.error('❌ Error loading min/max data:', error);
    return new Map();
  }
};

export const createSystemLookupMap = (minMaxData) => {
  console.log('📊 Creating system lookup map from min/max data');
  
  // minMaxData is already a Map from readMinMaxFile()
  return minMaxData;
};

export const matchSystemData = (inventoryItem, systemLookupMap) => {
  if (!systemLookupMap || !inventoryItem) {
    return { systemMin: null, systemMax: null };
  }
  
  // Normalize store number for consistent matching
  const cleanStore = normalizeStoreNumber(inventoryItem.site || inventoryItem.store);
  const cleanSku = String(inventoryItem.sku || '').trim();
  const key = `${cleanStore}-${cleanSku}`;
  
  const systemData = systemLookupMap.get(key);
  
  if (systemData) {
    return {
      systemMin: systemData.min,
      systemMax: systemData.max
    };
  }
  
  return { systemMin: null, systemMax: null };
};